import { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { SHOW_CONTROL_CENTRE_INFO_TOOLTIPS } from './controlCentreUiFlags';

interface PortalHelpTooltipProps {
  iconClassName?: string;
  children: ReactNode;
}

/**
 * Help icon tooltip rendered via portal so it is not clipped by card overflow,
 * grid siblings, or the fixed sidebar.
 *
 * Hidden when SHOW_CONTROL_CENTRE_INFO_TOOLTIPS is false (tooltip content is
 * kept in callers — flip the flag to restore icons without re-adding copy).
 */
export default function PortalHelpTooltip({
  iconClassName = 'w-5 h-5 text-ring cursor-help hover:scale-110 transition-transform',
  children,
}: PortalHelpTooltipProps) {
  if (!SHOW_CONTROL_CENTRE_INFO_TOOLTIPS) return null;
  return (
    <PortalHelpTooltipInner iconClassName={iconClassName}>
      {children}
    </PortalHelpTooltipInner>
  );
}

function PortalHelpTooltipInner({
  iconClassName,
  children,
}: {
  iconClassName: string;
  children: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const [style, setStyle] = useState<{ left: number; top: number; transform: string } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;

    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      const padding = 16;
      const tooltipWidth = Math.min(448, window.innerWidth * 0.9);
      const tooltipHeight = 280;
      const sidebarEl = document.querySelector('.fixed.left-0.top-0.h-screen');
      const sidebarWidth = sidebarEl?.getBoundingClientRect().width ?? 256;

      let left: number;
      if (rect.left + rect.width / 2 > window.innerWidth / 2) {
        left = rect.right - tooltipWidth;
      } else {
        left = rect.left;
      }
      left = Math.max(sidebarWidth + padding, left);
      left = Math.min(left, window.innerWidth - padding - tooltipWidth);

      let top = rect.bottom + padding;
      let transform = 'translateY(0)';
      if (top + tooltipHeight > window.innerHeight - padding && rect.top - padding - tooltipHeight > padding) {
        top = rect.top - padding;
        transform = 'translateY(-100%)';
      }

      setStyle({ left, top, transform });
    };

    const onEnter = () => {
      updatePosition();
      setShow(true);
    };

    const onLeave = () => {
      setShow(false);
      setStyle(null);
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, []);

  return (
    <>
      <div ref={anchorRef} className="inline-flex shrink-0">
        <HelpCircle className={iconClassName} />
      </div>
      {show && style && createPortal(
        <div
          className="fixed w-screen-90 max-w-md bg-popover border border-border rounded-xl p-4 shadow-2xl z-float max-h-screen-80 overflow-y-auto pointer-events-none"
          style={{
            left: `${style.left}px`,
            top: `${style.top}px`,
            transform: style.transform,
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  );
}
